import React, { useEffect, useState } from "react";
import { PlaylistItem, useActivePlaylist } from "../providers/PlaylistProvider";
import { LLayout } from "../templates/LLayout";
import { MediaOnly } from "../templates/MediaOnly";
import { Basic } from "../templates/Basic";
import { MultiScheduleLayout } from "../templates/MultiScheduleLayout";
import { Template } from "../../types";
import { ConfigProvider, useConfig } from "../providers/ConfigProvider";
import { AnnouncementsScroller } from "../components/AnnonucementsScroller";
import { ScreenFooter } from "../components/ScreenFooter";
import { useTranslation } from "react-i18next";
import { sortBy } from "lodash";
import { LoadingScreen } from "./LoadingScreen";
import { ErrorScreen } from "./ErrorScreen";
import { NotFoundLayout } from "../templates/NotFoundLayout";
import { Custom } from "../templates/Custom";
import { config as appConfig } from "../config";
import { Weather } from "../templates/Weather";
import { VerticalStacked } from "../templates/VerticalStacked";
import { VerticalMultiSchedule } from "../templates/VerticalMultiSchedule";
import { Strength } from "../templates/Strength";
import { VerticalStrength } from "../templates/VerticalStrength";

export const PlaylistScreen = () => {
  const { t } = useTranslation("PlaylistScreen");
  const { playlist } = useActivePlaylist();
  const [currentIndex, setCurrentIndex] = useState(0);

  const playlistItem = sortBy(playlist, (i) => i.orderId)[currentIndex];
  const currentConfigId = playlistItem?.configId;

  useEffect(() => {
    // playlist was updated, and the active index no longer exists
    if (currentConfigId == null && playlist.length > 0) {
      setCurrentIndex(0);
      return;
    }
  }, [currentConfigId, playlist]);

  if (playlist.length === 0) {
    return <ErrorScreen message={t("Empty playlist")} />;
  } else if (currentConfigId == null) {
    return <LoadingScreen message={t("Switching configuration")} />;
  }

  return (
    <>
      <AnnouncementsScroller />
      <ConfigProvider configId={currentConfigId}>
        <CurrentTemplate
          multiple={playlist.length > 1}
          playlistItem={playlistItem}
          onComplete={() => {
            setCurrentIndex((i) => {
              const next = i + 1;
              return next >= playlist.length ? 0 : next;
            });
          }}
        />
      </ConfigProvider>
      <ScreenFooter />
    </>
  );
};

const CurrentTemplate = ({
  onComplete,
  playlistItem,
  multiple,
}: {
  playlistItem?: PlaylistItem;
  onComplete: () => void;
  multiple: boolean;
}) => {
  const [loopCount, setLoopCount] = useState(0);

  useEffect(() => {
    // only want to loop through content when there are multiple items in the playlist
    if (!multiple) return;

    const loadNextConfig = () => {
      setLoopCount(0);
      onComplete();
    };

    if (playlistItem == null) {
      const timeout = setTimeout(loadNextConfig, appConfig.notFoundTimeout);
      return clearTimeout(timeout);
    }

    if (playlistItem.mode === "timed") {
      const timeout = setTimeout(loadNextConfig, playlistItem.duration * 1000);
      return () => clearTimeout(timeout);
    }

    if (
      playlistItem.mode === "looped" &&
      loopCount >= playlistItem.numberLoops
    ) {
      loadNextConfig();
    }
  }, [playlistItem, loopCount, onComplete, multiple]);

  return (
    <RenderedTemplate
      onLoopComplete={() => {
        if (playlistItem?.mode === "looped") {
          setLoopCount((c) => c + 1);
        }
      }}
    />
  );
};

const RenderedTemplate = (props: { onLoopComplete: () => void }) => {
  const config = useConfig();
  const TemplateComponent = templateComponent(
    config.status === "active" ? config.template : "not-found",
  );

  return (
    <TemplateComponent
      onLoopComplete={() => {
        if (config.status === "active") {
          props.onLoopComplete();
        }
      }}
    />
  );
};

const templateComponent = (template: Template | "not-found") => {
  switch (template) {
    case "l_layout":
      return LLayout;
    case "media_only":
      return MediaOnly;
    case "basic":
      return Basic;
    case "multi_day_schedule":
    case "multi_column_schedule":
      return MultiScheduleLayout;
    case "custom":
    case "nutrition":
      return Custom;
    case "strength":
      return Strength;
    case "vertical_strength":
      return VerticalStrength;
    case "weather":
      return Weather;
    case "vertical_stacked":
      return VerticalStacked;
    case "vertical_multi_day_schedule":
    case "vertical_multi_row_schedule":
      return VerticalMultiSchedule;
    case "not-found":
      return NotFoundLayout;
    default:
      throw new Error(`Template '${template ?? "undefined"}' not found`);
  }
};
